/**
 * Dr. Salus - Reminders Page
 * Lista, cria, edita e desativa lembretes WhatsApp.
 * Suporta schedule daily/weekly/monthly/once + variáveis dinâmicas.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, Plus, Edit2, Loader2, AlertCircle, Check, X, RefreshCw,
  ToggleLeft, ToggleRight, Trash2, Send,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import reminderService from '../../services/dr-salus/reminderService';
import templateService from '../../services/global/templateService';

const SCHEDULE_TYPES = [
  { value: 'daily', label: 'Diário' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'once', label: 'Único' },
];

const DAYS = [
  { value: 'MON', label: 'Seg' },
  { value: 'TUE', label: 'Ter' },
  { value: 'WED', label: 'Qua' },
  { value: 'THU', label: 'Qui' },
  { value: 'FRI', label: 'Sex' },
  { value: 'SAT', label: 'Sáb' },
  { value: 'SUN', label: 'Dom' },
];

const EMPTY_FORM = {
  phone: '',
  template_name: '',
  schedule_time: '09:00',
  schedule_type: 'daily',
  days_of_week: ['DAILY'],
  days_of_month: [],
  one_shot_date: '',
  template_variables: {},
};

function formatSchedule(reminder) {
  const t = reminder.schedule_time || '--:--';
  switch (reminder.schedule_type || 'daily') {
    case 'daily':
      if ((reminder.days_of_week || []).includes('DAILY')) return `Todo dia às ${t}`;
      return `${(reminder.days_of_week || []).join(', ')} às ${t}`;
    case 'weekly':
      return `Toda ${(reminder.days_of_week || []).map(d => DAYS.find(x => x.value === d)?.label || d).join(', ')} às ${t}`;
    case 'monthly':
      return `Dia ${(reminder.days_of_month || []).join(', ')} de cada mês às ${t}`;
    case 'once':
      return `Único: ${reminder.one_shot_date || '?'} às ${t}`;
    default:
      return t;
  }
}

export default function RemindersPage() {
  const { colors } = useTheme();
  const [reminders, setReminders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [templateDetail, setTemplateDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, t] = await Promise.all([reminderService.list(), templateService.list()]);
      setReminders(r.reminders || []);
      setTemplates((t.templates || []).filter(x => x.status === 'APPROVED'));
    } catch (e) {
      setError('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadTemplateDetail = useCallback(async (templateName) => {
    if (!templateName) {
      setTemplateDetail(null);
      return;
    }
    try {
      const detail = await reminderService.getTemplateDetail(templateName);
      setTemplateDetail(detail);
    } catch (e) {
      setTemplateDetail(null);
    }
  }, []);

  useEffect(() => {
    loadTemplateDetail(form.template_name);
  }, [form.template_name, loadTemplateDetail]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setTemplateDetail(null);
    setShowForm(true);
  };

  const openEdit = (reminder) => {
    setEditing(reminder);
    setForm({
      phone: reminder.phone,
      template_name: reminder.template_name,
      schedule_time: reminder.schedule_time,
      schedule_type: reminder.schedule_type || 'daily',
      days_of_week: reminder.days_of_week || [],
      days_of_month: reminder.days_of_month || [],
      one_shot_date: reminder.one_shot_date || '',
      template_variables: reminder.template_variables || {},
    });
    setShowForm(true);
  };

  const buildPayload = () => {
    const payload = {
      phone: form.phone,
      template_name: form.template_name,
      schedule_time: form.schedule_time,
      schedule_type: form.schedule_type,
      template_variables: form.template_variables || {},
    };
    if (form.schedule_type === 'daily' || form.schedule_type === 'weekly') {
      payload.days_of_week = form.days_of_week;
    }
    if (form.schedule_type === 'monthly') {
      payload.days_of_month = form.days_of_month;
    }
    if (form.schedule_type === 'once') {
      payload.one_shot_date = form.one_shot_date;
    }
    return payload;
  };

  const handleSave = async () => {
    if (!form.phone || !form.template_name) return;
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (editing) {
        await reminderService.update(editing.id, payload);
        setSuccessMsg('Lembrete atualizado!');
      } else {
        await reminderService.create(payload);
        setSuccessMsg('Lembrete criado!');
      }
      setShowForm(false);
      await load();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao salvar lembrete.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (reminder) => {
    if (!window.confirm(`Desativar lembrete para ${reminder.phone}?`)) return;
    try {
      await reminderService.deactivate(reminder.id);
      await load();
    } catch (e) {
      setError('Erro ao desativar lembrete.');
    }
  };

  const handleSendNow = async (reminder) => {
    try {
      const res = await reminderService.sendNow(reminder.id);
      if (res.success) {
        setSuccessMsg(`Lembrete enviado (${res.delivery_mode}).`);
      } else {
        setError(`Falha no envio: ${res.error || 'desconhecido'}`);
      }
      setTimeout(() => { setSuccessMsg(null); setError(null); }, 4000);
    } catch (e) {
      setError('Erro ao enviar lembrete.');
    }
  };

  const toggleDayOfWeek = (day) => {
    setForm(f => {
      const cur = f.days_of_week || [];
      const next = cur.includes(day) ? cur.filter(d => d !== day) : [...cur, day];
      return { ...f, days_of_week: next };
    });
  };

  const toggleDayOfMonth = (day) => {
    setForm(f => {
      const cur = f.days_of_month || [];
      const next = cur.includes(day) ? cur.filter(d => d !== day) : [...cur, day];
      return { ...f, days_of_month: next.sort((a, b) => a - b) };
    });
  };

  const setCustomVar = (key, value) => {
    setForm(f => ({ ...f, template_variables: { ...(f.template_variables || {}), [key]: value } }));
  };

  const s = {
    page: { padding: '24px', maxWidth: '1100px' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
    title: { fontSize: '20px', fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '10px' },
    btn: (variant = 'primary') => ({
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '8px 16px', borderRadius: '8px', border: 'none',
      cursor: 'pointer', fontSize: '13px', fontWeight: 600,
      backgroundColor: variant === 'primary' ? '#10b981' : colors.bgSecondary,
      color: variant === 'primary' ? '#fff' : colors.textPrimary,
    }),
    card: {
      backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
      borderRadius: '12px', padding: '16px 20px', marginBottom: '10px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    },
    cardName: { fontSize: '15px', fontWeight: 600, color: colors.textPrimary },
    cardMeta: { fontSize: '12px', color: colors.textSecondary, marginTop: '4px' },
    badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, marginLeft: '8px' },
    modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalBox: { backgroundColor: colors.bgPrimary, borderRadius: '16px', padding: '28px', width: '560px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
    label: { fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '6px', display: 'block' },
    input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: '14px', boxSizing: 'border-box' },
    select: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: '14px' },
    formRow: { marginBottom: '16px' },
    row: { display: 'flex', gap: '12px', marginBottom: '16px' },
    chip: (selected) => ({
      padding: '6px 12px', borderRadius: '16px', cursor: 'pointer',
      border: `1px solid ${selected ? '#10b981' : colors.borderColor}`,
      backgroundColor: selected ? '#10b98120' : colors.bgSecondary,
      color: selected ? '#10b981' : colors.textPrimary, fontSize: '12px', fontWeight: 600,
    }),
    iconBtn: { padding: '6px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent', color: colors.textSecondary },
    tabs: { display: 'flex', gap: '4px', borderBottom: `1px solid ${colors.borderColor}`, marginBottom: '16px' },
    tab: (active) => ({
      padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
      color: active ? '#10b981' : colors.textSecondary,
      borderBottom: active ? '2px solid #10b981' : '2px solid transparent',
    }),
    varBlock: { padding: '12px', backgroundColor: colors.bgSecondary, borderRadius: '8px', marginBottom: '10px' },
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>
          <Bell size={22} color="#10b981" />
          Lembretes Dr. Salus
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={s.btn('secondary')} onClick={load}>
            <RefreshCw size={14} /> Atualizar
          </button>
          <button style={s.btn('primary')} onClick={openCreate}>
            <Plus size={14} /> Novo Lembrete
          </button>
        </div>
      </div>

      {successMsg && (
        <div style={{ padding: '10px 16px', backgroundColor: '#10b98120', color: '#10b981', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Check size={16} /> {successMsg}
        </div>
      )}
      {error && (
        <div style={{ padding: '10px 16px', backgroundColor: '#ef444420', color: '#ef4444', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Loader2 size={24} color="#10b981" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : reminders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: colors.textSecondary }}>
          Nenhum lembrete criado ainda.
        </div>
      ) : (
        reminders.map(r => (
          <div key={r.id} style={{ ...s.card, opacity: r.active ? 1 : 0.5 }}>
            <div style={{ flex: 1 }}>
              <div style={s.cardName}>
                {r.phone}
                <span style={{ ...s.badge, backgroundColor: '#10b98120', color: '#10b981' }}>{r.template_name}</span>
                {!r.active && <span style={{ ...s.badge, backgroundColor: '#6b728020', color: '#6b7280' }}>inativo</span>}
              </div>
              <div style={s.cardMeta}>{formatSchedule(r)}</div>
              {Object.keys(r.template_variables || {}).length > 0 && (
                <div style={s.cardMeta}>
                  Variáveis: {Object.entries(r.template_variables).map(([k, v]) => `${k}=${v}`).join(' · ')}
                </div>
              )}
              {r.last_sent_date && <div style={s.cardMeta}>Último envio: {r.last_sent_date}</div>}
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button style={s.iconBtn} onClick={() => handleSendNow(r)} title="Testar agora">
                <Send size={15} />
              </button>
              <button style={s.iconBtn} onClick={() => openEdit(r)} title="Editar">
                <Edit2 size={15} />
              </button>
              <button style={s.iconBtn} onClick={() => handleDeactivate(r)} title="Desativar">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))
      )}

      {showForm && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                {editing ? 'Editar Lembrete' : 'Novo Lembrete'}
              </h2>
              <button style={s.iconBtn} onClick={() => setShowForm(false)}>
                <X size={18} />
              </button>
            </div>

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Telefone (E.164 sem +)</label>
                <input style={s.input} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="5511999999999" disabled={!!editing} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Horário (SP)</label>
                <input style={s.input} type="time" value={form.schedule_time} onChange={e => setForm(f => ({ ...f, schedule_time: e.target.value }))} />
              </div>
            </div>

            <div style={s.formRow}>
              <label style={s.label}>Template aprovado</label>
              <select style={s.select} value={form.template_name} onChange={e => setForm(f => ({ ...f, template_name: e.target.value }))} disabled={!!editing}>
                <option value="">Selecione...</option>
                {templates.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            </div>

            {templateDetail && templateDetail.variables?.length > 0 && (
              <div style={s.varBlock}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: colors.textPrimary, marginBottom: '10px' }}>
                  Variáveis deste template
                </div>
                {templateDetail.variables.map(v => {
                  const isCustom = v.source.startsWith('custom:');
                  const key = isCustom ? v.source.substring('custom:'.length) : v.key;
                  return (
                    <div key={v.position} style={{ marginBottom: '8px' }}>
                      <label style={s.label}>
                        {`{{${v.position}}}`} {v.label} <span style={{ color: colors.textSecondary, fontWeight: 400 }}>({v.source})</span>
                      </label>
                      {isCustom ? (
                        <input
                          style={s.input}
                          value={(form.template_variables || {})[key] || ''}
                          onChange={e => setCustomVar(key, e.target.value)}
                          placeholder={`Valor para ${key}`}
                        />
                      ) : (
                        <div style={{ ...s.cardMeta, fontStyle: 'italic' }}>(resolvido automaticamente em runtime)</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={s.tabs}>
              {SCHEDULE_TYPES.map(t => (
                <div key={t.value} style={s.tab(form.schedule_type === t.value)} onClick={() => setForm(f => ({ ...f, schedule_type: t.value }))}>
                  {t.label}
                </div>
              ))}
            </div>

            {form.schedule_type === 'daily' && (
              <div style={s.formRow}>
                <label style={s.label}>Dias da semana (ou ative "Todos")</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <div style={s.chip(form.days_of_week.includes('DAILY'))} onClick={() => setForm(f => ({ ...f, days_of_week: f.days_of_week.includes('DAILY') ? [] : ['DAILY'] }))}>
                    Todos os dias
                  </div>
                  {DAYS.map(d => (
                    <div key={d.value} style={s.chip(form.days_of_week.includes(d.value))} onClick={() => toggleDayOfWeek(d.value)}>
                      {d.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {form.schedule_type === 'weekly' && (
              <div style={s.formRow}>
                <label style={s.label}>Dias da semana</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {DAYS.map(d => (
                    <div key={d.value} style={s.chip(form.days_of_week.includes(d.value))} onClick={() => toggleDayOfWeek(d.value)}>
                      {d.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {form.schedule_type === 'monthly' && (
              <div style={s.formRow}>
                <label style={s.label}>Dias do mês</label>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <div key={d} style={{ ...s.chip(form.days_of_month.includes(d)), minWidth: '32px', textAlign: 'center', padding: '4px 8px' }} onClick={() => toggleDayOfMonth(d)}>
                      {d}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {form.schedule_type === 'once' && (
              <div style={s.formRow}>
                <label style={s.label}>Data do envio</label>
                <input style={s.input} type="date" value={form.one_shot_date} onChange={e => setForm(f => ({ ...f, one_shot_date: e.target.value }))} />
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button style={s.btn('secondary')} onClick={() => setShowForm(false)} disabled={saving}>
                Cancelar
              </button>
              <button style={s.btn('primary')} onClick={handleSave} disabled={saving || !form.phone || !form.template_name}>
                {saving ? <Loader2 size={14} /> : <Check size={14} />}
                {editing ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
